import { css } from '@emotion/react';
import { breakpointInfix, breakpointNext, MediaBreakpointDown, MediaBreakpointUp } from './mixins/_breakpoints.mixin';
import { FontSize } from './vendor/_rfs.mixin';
import { HoverFocus } from './mixins/_hover.mixin';
import { BorderRadius } from './mixins/_border-radius.mixin';
import { escapeSVG } from './_functions';

export default $ => {
  const rules = [];

  // Because flex properties aren't inherited, we need to redeclare these first
  // few properties so that content nested within behave properly.
  const containerFlexProperties = css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
  `;

  // Navbar
  //
  // Provide a static navbar from which we expand to create full-width, fixed, and
  // other navbar variations.
  rules.push(css`
    .navbar {
      position: relative;
      display: flex;
      flex-wrap: wrap; // allow us to do the line break for collapsing content
      align-items: center;
      justify-content: space-between; // space out brand from logo
      padding: ${$.navbarPaddingY} ${$.navbarPaddingX};

      .container,
      .container-fluid {
        ${containerFlexProperties};
      }

      ${css(
        Object.keys($.containerMaxWidths).map(
          breakpoint => css`
            > .container${breakpointInfix($, breakpoint, $.containerMaxWidths)} {
              ${containerFlexProperties};
            }
          `
        )
      )}
    }
  `);

  // Navbar brand
  //
  // Used for brand, project, or site names.
  rules.push(css`
    .navbar-brand {
      display: inline-block;
      padding-top: ${$.navbarBrandPaddingY};
      padding-bottom: ${$.navbarBrandPaddingY};
      margin-right: ${$.navbarPaddingX};
      ${new FontSize($.navbarBrandFontSize).include($)};
      line-height: inherit;
      white-space: nowrap;

      ${new HoverFocus().include(
        $,
        css`
          text-decoration: none;
        `
      )}
    }
  `);

  // Navbar nav
  //
  // Custom navbar navigation (doesn't require `.nav`, but does make use of `.nav-link`).
  rules.push(css`
    .navbar-nav {
      display: flex;
      flex-direction: column; // cannot use \`inherit\` to get the \`.navbar\`s value
      padding-left: 0;
      margin-bottom: 0;
      list-style: none;

      .nav-link {
        padding-right: 0;
        padding-left: 0;
      }

      .dropdown-menu {
        position: static;
        float: none;
      }
    }
  `);

  // Navbar text
  //
  //
  rules.push(css`
    .navbar-text {
      display: inline-block;
      padding-top: ${$.navLinkPaddingY};
      padding-bottom: ${$.navLinkPaddingY};
    }
  `);

  // Responsive navbar
  //
  // Custom styles for responsive collapsing and toggling of navbar contents.
  // Powered by the collapse Bootstrap JavaScript plugin.

  // When collapsed, prevent the toggleable navbar contents from appearing in
  // the default flexbox row orientation. Requires the use of `flex-wrap: wrap`
  // on the `.navbar` parent.
  rules.push(css`
    .navbar-collapse {
      flex-basis: 100%;
      flex-grow: 1;
      // For always expanded or extra full navbars, ensure content aligns itself
      // properly vertically. Can be easily overridden with flex utilities.
      align-items: center;
    }
  `);

  // Button for toggling the navbar when in its collapsed state
  rules.push(css`
    .navbar-toggler {
      padding: ${$.navbarTogglerPaddingY} ${$.navbarTogglerPaddingX};
      ${new FontSize($.navbarTogglerFontSize).include($)};
      line-height: 1;
      background-color: transparent; // remove default button style
      border: ${$.borderWidth} solid transparent; // remove default button style
      ${new BorderRadius($.navbarTogglerBorderRadius).include($)};

      ${new HoverFocus().include(
        $,
        css`
          text-decoration: none;
        `
      )}
    }
    @media only screen and (min-width: 850px) {
      .navbar-toggler {
        display: none;
      }
    }
  `);

  // Keep as a separate element so folks can easily override it with another icon
  // or image file as needed.
  rules.push(css`
    .navbar-toggler-icon {
      display: inline-block;
      width: 1.5em;
      height: 1.5em;
      vertical-align: middle;
      content: '';
      background: 50% / 100% 100% no-repeat;
    }
  `);

  rules.push(css`
    .navbar-nav-scroll {
      max-height: ${$.navbarNavScrollMaxHeight};
      overflow-y: auto;
    }
  `);

  // Generate series of `.navbar-expand-*` responsive classes for configuring
  // where your navbar collapses.
  rules.push(css`
    .navbar-expand {
      ${Object.keys($.gridBreakpoints).map($breakpoint => {
        const $next = breakpointNext($, $breakpoint, $.gridBreakpoints);
        const $infix = breakpointInfix($, $next, $.gridBreakpoints);
        const containerNavbarExpand = css`
          padding-right: 0;
          padding-left: 0;
        `;

        // For nesting containers, have to redeclare for alignment purposes
        const containerNesting = css`
          flex-wrap: nowrap;
        `;

        return css`
          &${$infix} {
            ${new MediaBreakpointDown($breakpoint).include(
              $,
              css`
                > .container,
                > .container-fluid {
                  ${containerNavbarExpand};
                }

                ${Object.keys($.containerMaxWidths).map($size => {
                  return css`
                    > .container${breakpointInfix($, $size, $.containerMaxWidths)} {
                      ${containerNavbarExpand};
                    }
                  `;
                })}
              `
            )}

            ${new MediaBreakpointUp($next).include(
              $,
              css`
                flex-flow: row nowrap;
                justify-content: flex-start;

                .navbar-nav {
                  flex-direction: row;

                  .dropdown-menu {
                    position: absolute;
                  }

                  .nav-link {
                    padding-right: ${$.navbarNavLinkPaddingX};
                    padding-left: ${$.navbarNavLinkPaddingX};
                  }
                }

                > .container,
                > .container-fluid {
                  ${containerNesting};
                }

                ${Object.keys($.containerMaxWidths).map(
                  size => css`
                    > .container${breakpointInfix($, size, $.containerMaxWidths)} {
                      ${containerNesting};
                    }
                  `
                )}

                .navbar-nav-scroll {
                  overflow: visible;
                }

                .navbar-collapse {
                  display: flex !important; // stylelint-disable-line declaration-no-important

                  // Changes flex-bases to auto because of an IE10 bug
                  flex-basis: auto;
                }

                .navbar-toggler {
                  display: none;
                }
              `
            )}
          }
        `;
      })}
    }
  `);

  // Navbar themes
  //
  // Styles for switching between navbars with light or dark background.

  // Dark links against a light background
  rules.push(css`
    .navbar-light {
      .navbar-brand {
        color: ${$.navbarLightBrandColor};

        ${new HoverFocus().include(
          $,
          css`
            color: ${$.navbarLightBrandHoverColor};
          `
        )}
      }

      .navbar-nav {
        .nav-link {
          color: ${$.navbarLightColor};

          ${new HoverFocus().include(
            $,
            css`
              color: ${$.navbarLightHoverColor};
            `
          )}

          &.disabled {
            color: ${$.navbarLightDisabledColor};
          }
        }

        .show > .nav-link,
        .active > .nav-link,
        .nav-link.show,
        .nav-link.active {
          color: ${$.navbarLightActiveColor};
        }
      }

      .navbar-toggler {
        color: ${$.navbarLightColor};
        border-color: ${$.navbarLightTogglerBorderColor};
      }

      .navbar-toggler-icon {
        background-image: ${escapeSVG($.navbarLightTogglerIconBg)};
      }

      .navbar-text {
        color: ${$.navbarLightColor};
        a {
          color: ${$.navbarLightActiveColor};

          ${new HoverFocus().include(
            $,
            css`
              color: ${$.navbarLightActiveColor};
            `
          )}
        }
      }
    }
  `);

  // White links against a dark background
  rules.push(css`
    .navbar-dark {
      .navbar-brand {
        color: ${$.navbarDarkBrandColor};

        ${new HoverFocus().include(
          $,
          css`
            color: ${$.navbarDarkBrandHoverColor};
          `
        )}
      }

      .navbar-nav {
        .nav-link {
          color: ${$.navbarDarkColor};

          ${new HoverFocus().include(
            $,
            css`
              color: ${$.navbarDarkHoverColor};
            `
          )}

          &.disabled {
            color: ${$.navbarDarkDisabledColor};
          }
        }

        .show > .nav-link,
        .active > .nav-link,
        .nav-link.show,
        .nav-link.active {
          color: ${$.navbarDarkActiveColor};
        }
      }

      .navbar-toggler {
        color: ${$.navbarDarkColor};
        border-color: ${$.navbarDarkTogglerBorderColor};
      }

      .navbar-toggler-icon {
        background-image: ${escapeSVG($.navbarDarkTogglerIconBg)};
      }

      .navbar-text {
        color: ${$.navbarDarkColor};
        a {
          color: ${$.navbarDarkActiveColor};

          ${new HoverFocus().include(
            $,
            css`
              color: ${$.navbarDarkActiveColor};
            `
          )}
        }
      }
    }
  `);

  return css(rules);
};
